import React, { useEffect, useState } from 'react'
import { Box, Grid, Stack, TextField, Typography, Card, IconButton, Accordion, AccordionSummary, AccordionDetails, Select, MenuItem, Chip } from '@mui/material';
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
// icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AngleRightIcon from "common/customIcons/angle_right";
import { grey } from '@mui/material/colors';
// utils and helpers
import { formattedDate } from "utilities/common";
import { addDistributionManagement, getDistributionManagementDetails, getTemplateKits } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { openSnackbar } from "../../../../redux/slices/common";
import { getRegions } from "../../../../redux/slices/hyperwolf/settings";
// custom components
import MDButton from 'components/MDButton';
import { distributionBoxValidation } from "validations/hyperwolf/distributionmanagement";
import CommonLayout from "layouts/DashboardLayout";

const AddDistribution = () => {
    const { distributionId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate()

    const [details, setDetails] = useState({});
    const [errors, setErrors] = useState({});
    const [expanded, setExpanded] = useState(false);
    const [templateKits, setTemplateKits] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [regionsList, setRegionsList] = useState([]);

    const handleRegionsList = async (filters = {}) => {
        try {
            const response = await dispatch(getRegions({ ...filters, limit: 100 })).unwrap()
            if (response?.data) {
                setRegionsList(response?.data || [])
            }
        } catch (error) {
            const errorMsg = error?.response?.data?.message || error?.message || error
            dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
        }
    }

    const handleExpand = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleChange = (i) => (event) => {
        const { name, value } = event.target;
        let updatedValue = value;

        setTemplateKits(prev => {
            const newKits = [...prev];
            if (['minProductPerRegion', 'maxProductPerRegion']?.includes(name)) {
                updatedValue = updatedValue === "" ? "" : updatedValue?.replace(/[^0-9]/g, "");
            }

            newKits[i] = { ...newKits[i], [name]: updatedValue };
            return newKits;
        })
    };

    const handleTopLevelChange = (name, i) => (event) => {
        const { value } = event.target;

        setTemplateKits(prev => {
            const newKits = [...prev];

            newKits[i] = { ...newKits[i], [name]: value };
            return newKits;
        })
    };

    const handleBoxChange = (i, index) => (event,) => {
        const { name, value } = event.target;
        setTemplateKits(prev => {
            const newKits = [...prev];
            newKits[i].boxes[index] = { ...newKits[i].boxes[index], [name]: value };

            return newKits;
        })
    };

    const handleTemplateKits = async (filters = {}) => {
        try {
            const response = await dispatch(getTemplateKits({ ...filters, limit: 100 })).unwrap()
            if (response?.data) {
                setTemplateKits(response?.data || [])
            }
        } catch (error) {
            const errorMsg = error?.response?.data?.message || error?.message || error
            dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
        }
    };

    const handleDistributionDetails = async () => {
        try {
            const response = await dispatch(getDistributionManagementDetails({ distributionId })).unwrap()
            if (response?.data) {
                setTemplateKits(response?.data)
            }
        } catch (error) {
            const errorMsg = error?.response?.data?.message || error?.message || error
            dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
        }
    };

    const handleSave = async () => {
        if (isLoading) return;
        setIsLoading(true);

        try {
            const normalizedDetails = {
                ...details,
                kits: (templateKits || []).map((kit) => {
                    const { regionId, subRegionId, minProductPerRegion, maxProductPerRegion, boxes } = kit;
                    return {
                        regionId,
                        subRegionId,
                        minProductPerRegion,
                        maxProductPerRegion,
                        boxes: boxes.map(box => ({ categoryId: box.categoryId, maxProductValuePerBox: box.maxProductValuePerBox, minProductValuePerBox: box.minProductValuePerBox }))
                    };
                })
            };

            const { isValid, myErrors } = distributionBoxValidation(normalizedDetails);
            if (!isValid) {
                setErrors(myErrors);
                return;
            } else {
                setErrors({});
            }

            return;

            const response = await dispatch(addDistributionManagement({ ...templateKits })).unwrap();
            if (response?.data) {
                navigate("/hyperwolf/distribution-management")
            }
        } catch (error) {
            const errorMsg = error?.response?.data?.message || error?.message || error
            dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleRegionsList();
    }, [])

    useEffect(() => {
        if (!distributionId) {
            handleTemplateKits();
        } else {
            handleDistributionDetails();
        }
    }, [distributionId]);

    return (
        <CommonLayout>
            <Box p={{ xs: 2, sm: 3, lg: 4 }} className="cus-contents">
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                    gap={2}
                    mb={2}
                >
                    <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
                        <IconButton onClick={() => navigate("/hyperwolf/distribution-management")}>
                            <AngleRightIcon fontSize="14px" />
                        </IconButton>
                        <Typography variant="h6" fontWeight="semibold" ml={0.75}>
                            Distribution List
                        </Typography>
                    </Box>
                </Stack>

                <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
                    <Grid container spacing={3}>
                        {/* Distribution Date */}
                        <Grid item xs={12}>
                            <Typography variant="h5" fontWeight="semibold" mb={2}>
                                Add Distribution
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Distribution Date</Typography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        className="form-input created-by p-l-field bg-transparent"
                                        disabled
                                        name=""
                                        value={formattedDate(details?.createdDate || new Date())}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <hr className='my-3' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction='row' gap={1} alignItems='center' justifyContent="space-between">
                                <Typography variant="h5" fontWeight="semibold">Region</Typography>
                                <MDButton
                                    variant="contained"
                                    sx={{ minWidth: '150px', background: "#2E2E3A !important", color: "#ffffff !important", fontSize: "14px", minHeight: "36px", padding: "7px 20px" }}
                                    onClick={() => navigate(`/hyperwolf/distribution-management/add-region-kit/${distributionId}`)}
                                >
                                    + Add Region
                                </MDButton>
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            {
                                templateKits?.map((templateKit, i) => (
                                    <Accordion
                                        key={templateKit._id}
                                        className="cus-accordion-summary"
                                        expanded={expanded === templateKit._id}
                                        onChange={handleExpand(templateKit._id)}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="panel1d-content"
                                            id="panel1d-header"
                                        >
                                            <Typography>{templateKit?.region?.region}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={3}>
                                                            <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Region ID</Typography>
                                                            <Select
                                                                size='large'
                                                                fullWidth
                                                                className='form-select'
                                                                name="region"
                                                                value={templateKit?.regionId}
                                                                onChange={handleChange(i)}
                                                                MenuProps={{
                                                                    classes: {
                                                                        root: "cus-dropdown-height",
                                                                    }
                                                                }}
                                                            >
                                                                {
                                                                    regionsList?.map(region => (
                                                                        <MenuItem key={region.regionId} value={region.regionId}>{region?.region}</MenuItem>
                                                                    ))
                                                                }
                                                            </Select>
                                                        </Grid>
                                                        <Grid item xs={12} md={3} className="cus-multi-select">
                                                            <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Sub Region ID</Typography>
                                                            <Select
                                                                size='large'
                                                                fullWidth
                                                                className='form-select'
                                                                label=''
                                                                multiple={true}
                                                                name="subRegionId"
                                                                value={templateKit.subRegionId}
                                                                onChange={handleTopLevelChange('subRegionId', i)}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {templateKit?.subRegionId.map((subRegionId) => {
                                                                            const subRegion = templateKit?.subRegion?.find(subRegion => subRegion._id === subRegionId);
                                                                            if (!subRegion) return null;

                                                                            return (
                                                                                <Chip
                                                                                    key={subRegion.subRegionId}
                                                                                    label={subRegion.name}
                                                                                    onDelete={(event) => {
                                                                                        event.stopPropagation();
                                                                                        // Remove the driverId from the array and update
                                                                                        const newSubRegions = selected?.filter(id => id !== subRegion._id);
                                                                                        // Simulate a synthetic event for handleSubRegionChange
                                                                                        handleTopLevelChange('subRegionId', i)({ target: { name: "subRegionId", value: newSubRegions } })
                                                                                    }}
                                                                                    sx={{ m: 0.25 }}
                                                                                    // Prevent chip click from opening dropdown
                                                                                    onMouseDown={e => e.stopPropagation()}
                                                                                    onClick={e => e.stopPropagation()}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {
                                                                    templateKit?.subRegion?.map(subRegion => (
                                                                        <MenuItem key={subRegion?._id} value={subRegion._id}>
                                                                            {subRegion.name}
                                                                        </MenuItem>
                                                                    ))
                                                                }
                                                            </Select>
                                                            <p className="error">{errors?.[i]?.subRegionId}</p>
                                                        </Grid>
                                                        <Grid item xs={12} md={3}>
                                                            <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Min Products Per Region</Typography>
                                                            <TextField
                                                                fullWidth
                                                                variant="outlined"
                                                                className="form-input created-by p-l-field bg-transparent"
                                                                name="minProductPerRegion"
                                                                value={templateKit?.minProductPerRegion}
                                                                onChange={handleChange(i)}
                                                            />
                                                            <p className="error">{errors?.[i]?.minProductPerRegion}</p>
                                                        </Grid>
                                                        <Grid item xs={12} md={3}>
                                                            <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Max Products Per Region</Typography>
                                                            <TextField
                                                                fullWidth
                                                                variant="outlined"
                                                                className="form-input created-by p-l-field bg-transparent"
                                                                name="maxProductPerRegion"
                                                                value={templateKit?.maxProductPerRegion}
                                                                onChange={handleChange(i)}
                                                            />
                                                            <p className="error">{errors?.[i]?.maxProductPerRegion}</p>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Add Boxes For Specific Limits</Typography>
                                                    {
                                                        templateKit.boxes?.map((box, ind) => (
                                                            <Grid key={box._id} container spacing={3} className="mt-0">
                                                                <Grid item xs={12} md={4}>
                                                                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Box</Typography>
                                                                    <Select
                                                                        size='large'
                                                                        fullWidth
                                                                        className='form-select'
                                                                        name="categoryId"
                                                                        value={box.categoryId}
                                                                        MenuProps={{
                                                                            classes: {
                                                                                root: "cus-dropdown-height",
                                                                            }
                                                                        }}
                                                                    >
                                                                        <MenuItem value={box.category?.id}>
                                                                            {box.category?.name}
                                                                        </MenuItem>
                                                                    </Select>
                                                                    <p className="error">{errors?.[i]?.boxes?.[ind]?.categoryId}</p>
                                                                </Grid>
                                                                <Grid item xs={12} md={4}>
                                                                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Min Products Values For This Box</Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        variant="outlined"
                                                                        className="form-input created-by p-l-field bg-transparent"
                                                                        name="minProductValuePerBox"
                                                                        value={box.minProductValuePerBox}
                                                                        onChange={handleBoxChange(i, ind)}
                                                                    />
                                                                    <p className="error">{errors?.[i]?.boxes?.[ind]?.minProductValuePerBox}</p>
                                                                </Grid>
                                                                <Grid item xs={12} md={4}>
                                                                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Max Products Values For This Box</Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        variant="outlined"
                                                                        className="form-input created-by p-l-field bg-transparent"
                                                                        name="maxProductValuePerBox"
                                                                        value={box.maxProductValuePerBox}
                                                                        onChange={handleBoxChange(i, ind)}
                                                                    />
                                                                    <p className="error">{errors?.[i]?.boxes?.[ind]?.maxProductValuePerBox}</p>
                                                                </Grid>
                                                            </Grid>
                                                        ))
                                                    }
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            }
                        </Grid>

                        {/********************** Save, Cancel Buttons **********************/}
                        <Grid item xs={12}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={2}>
                                <MDButton
                                    sx={{ minWidth: "160px" }}
                                    type="button"
                                    variant="contained"
                                    color="primary"
                                    disabled={isLoading}
                                    onClick={handleSave}
                                >
                                    Save
                                </MDButton>
                                <MDButton
                                    sx={{ minWidth: "160px" }}
                                    type="button"
                                    variant="outlined"
                                    color="dark"
                                    onClick={() => navigate("/hyperwolf/distribution-management")}
                                >
                                    Cancel
                                </MDButton>
                            </Stack>
                        </Grid>
                    </Grid>
                </Card>
            </Box>
        </CommonLayout>
    )
}

export default AddDistribution;